import { formatQuantity, formatPricingValue } from "common/formatters";
import { Unit, UnitConversionRate } from "./unit";
import * as NP from "number-precision";
import { CommodityUnit } from "./commodityUnit";

class UnitHelper {

    public getQuantity(quantity: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[], precision: number): number {
        if (commodity.baseUnit.id === commodity.localUnit.id) return quantity;

        const localQuantity = this.getLocalQuantity(quantity, commodity, unitsConversionRates, precision);
        return localQuantity ?? quantity;
    }

    public getQuantityTextWithUnits(quantity: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[], precision: number): string {
        const baseQuantityText = this.getQuantityWithUnits(quantity, commodity.baseUnit, precision);
        if (commodity.baseUnit.id === commodity.localUnit.id) return baseQuantityText;

        const localQuantity = this.getLocalQuantity(quantity, commodity, unitsConversionRates, precision);
        return localQuantity == null
            ? baseQuantityText
            : `${baseQuantityText} / ${this.getQuantityWithUnits(localQuantity, commodity.localUnit, precision)}`;
    }

    public getLocalQuantityTextWithUnits(quantity: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[], precision: number): string {
        const localQuantity = this.getLocalQuantity(quantity, commodity, unitsConversionRates, precision);
        return localQuantity == null
            ? null
            : this.getQuantityWithUnits(localQuantity, commodity.localUnit, precision);
    }

    public getLocalQuantity(quantity: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[], precision: number): number {
        return this.getConvertedQuantity(quantity, commodity.id, commodity.baseUnit.id, commodity.localUnit.id, unitsConversionRates, precision);
    }

    public getBaseQuantity(quantity: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[], precision: number): number {
        return this.getConvertedQuantity(quantity, commodity.id, commodity.localUnit.id, commodity.baseUnit.id, unitsConversionRates, precision);
    }

    public getBaseQuantityTextWithUnits(quantity: number, commodity: CommodityUnit, precision: number): string {
        return this.getQuantityWithUnits(quantity, commodity.baseUnit, precision);
    }

    private getConvertedQuantity(quantity: number, commodityId: number, fromUnitId: number, toUnitId: number, unitsConversionRates: UnitConversionRate[], precision: number): number {
        if (quantity == null || quantity === 0) return quantity;

        const conversionRate = unitsConversionRates?.find(c => c.commodityId === commodityId &&
            c.fromUnitId === fromUnitId && c.toUnitId === toUnitId)?.conversionRate;

        if (conversionRate == null) return null;

        return Number(NP.times(quantity, conversionRate).toFixed(precision));
    }

    private getQuantityWithUnits(quantity: number, unit: Unit, precision: number): string {
        return `${formatQuantity(quantity, precision)} ${unit.abbreviation}`;
    }

    public getLocalPriceText(price: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[]): string {
        const priceText = formatPricingValue(price);
        const localPrice = this.getLocalPrice(price, commodity, unitsConversionRates);
        return `${priceText} ${price != null && localPrice != null ? `/ ${formatPricingValue(localPrice)}` : ""}`;
    }

    public getLocalPrice(price: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[]): number {
        return this.getConvertedPrice(price, commodity.id, commodity.baseUnit.id, commodity.localUnit.id, unitsConversionRates);
    }

    public getBasePrice(price: number, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[]): number {
        return this.getConvertedPrice(price, commodity.id, commodity.localUnit.id, commodity.baseUnit.id, unitsConversionRates);
    }

    public getPriceText(price: number, showLocalUOM: boolean, currencyCode: string, commodity: CommodityUnit, unitsConversionRates: UnitConversionRate[]): string {
        return `${formatPricingValue(price)} ${currencyCode} ${showLocalUOM ? `/ ${this.getLocalPrice(price, commodity, unitsConversionRates)} ${currencyCode}` : ""}`;
    }

    private getConvertedPrice(price: number, commodityId: number, fromUnitId: number, toUnitId: number, unitsConversionRates: UnitConversionRate[]): number {
        if (price == null || price === 0) return price;

        const conversionRate = unitsConversionRates?.find(c => c.commodityId === commodityId &&
            c.fromUnitId === fromUnitId && c.toUnitId === toUnitId)?.conversionRate;

        if (conversionRate == null) return null;

        return Number(NP.divide(price, conversionRate).toFixed(5));
    }
}

const unitHelper = new UnitHelper()

export default unitHelper;